import { setEntitySceneLink } from "../lib/timelineEntities";
import { computed, shallowRef } from "vue";
import { assertNever } from "../lib/assertNever";
import {
  fromMaybe,
  isJust,
  isNothing,
  just,
  mapMaybe,
  nothing,
  toMaybe,
  type Maybe,
} from "../lib/maybe";
import {
  linkArcMomentToScene,
  scenePathForArcMomentDrop,
  unlinkArcMomentFromScene,
} from "../lib/storyboardArcs";
import { dispatchInto } from "../lib/tea";
import { updateProjectDatabaseEntryFields } from "../lib/tauri/database";
import { acceptHMRUpdate, defineStore } from "pinia";
import { useActiveEntityFieldsStore } from "./useActiveEntityFieldsStore";
import { useEditorSessionStore } from "./useEditorSessionStore";
import { useOutlineEntitiesStore } from "./useOutlineEntitiesStore";
import { useProjectWorkspaceStore } from "./useProjectWorkspaceStore";
import {
  candidatePromotes,
  END_OF_MANUSCRIPT_DROP_TARGET,
  initialStoryboardDragModel,
  updateStoryboardDrag,
  type BoardCell,
  type BoardDragSource,
  type BoardHit,
  type StoryboardDragCmd,
  type StoryboardDragMsg,
} from "./useStoryboardDragStore.update";

export { END_OF_MANUSCRIPT_DROP_TARGET, type BoardCell, type BoardDragSource };

/** Max distance (px) the nearest-cell gutter fallback will reach. Keeps a
 * drop in truly empty space (well outside the grid) from snapping to
 * whatever cell happens to be closest. */
const NEAREST_CELL_MAX_PX = 64;

/**
 * Pointer-based reorder for the Storyboard, reusing `useOutlineDrag.ts`'s
 * state-machine shape (candidate → threshold-promote → pointermove/up/cancel
 * → commit → cleanup) but with a genuinely different hit test: a card grid
 * has real horizontal gutters between columns that a single-column list
 * never does, so a strict-containment-only test (correct for the outline)
 * would produce dead zones here. Falls back to nearest-cell-by-distance,
 * restricted to the band the pointer is vertically inside so a gutter drop
 * can never jump to a different chapter.
 *
 * Pinia-global so the board (main pane) and the Unplaced panel (sidebar),
 * router-view siblings, share the same drag state. `cellAtPoint`'s
 * `[data-board-key]` query is document-wide by design, so any element
 * anywhere on screen carrying the right `data-board-*` attributes
 * participates in the same drag.
 *
 * TEA: drag session model lives in `useStoryboardDragStore.update.ts`.
 * DOM hit-testing and workspace/database effects stay in this shell.
 */
export const useStoryboardDragStore = defineStore("storyboardDrag", () => {
  const session = useEditorSessionStore();
  const workspace = useProjectWorkspaceStore();
  const outlineEntities = useOutlineEntitiesStore();
  const activeEntityFields = useActiveEntityFieldsStore();
  const chapters = computed(() => session.chapters);

  const model = shallowRef(initialStoryboardDragModel);

  // Writable so existing callers and test harnesses can keep assigning them;
  // each write dispatches a plain setter Msg.
  const dragFrom = computed<Maybe<BoardDragSource>>({
    get: () => model.value.dragFrom,
    set: (value) => {
      void send({ type: "DragFromSet", value });
    },
  });
  const dragOver = computed<Maybe<string>>({
    get: () => model.value.dragOver,
    set: (value) => {
      void send({ type: "DragOverSet", value });
    },
  });
  const dragKind = computed<Maybe<BoardDragSource["kind"]>>({
    get: () => model.value.dragKind,
    set: (value) => {
      void send({ type: "DragKindSet", value });
    },
  });

  function send(msg: StoryboardDragMsg): Promise<void> {
    return dispatchInto(model, msg, updateStoryboardDrag, runCmd);
  }

  /** The band whose vertical span contains `clientY`, identified by its own
   * `[data-board-band]` element. Used to keep a gutter-fallback drop from
   * ever resolving to a cell in a different chapter's band. `nothing` means
   * the point isn't within any band (e.g. over a flat top-level card, the
   * Unplaced sidebar, or the page margins), in which case the nearest-cell
   * fallback isn't restricted. */
  function bandFolderAtY(clientY: number): Maybe<string> {
    const bands = document.querySelectorAll<HTMLElement>("[data-board-band]");
    for (const el of bands) {
      const rect = el.getBoundingClientRect();
      if (clientY >= rect.top && clientY <= rect.bottom) {
        return toMaybe(el.dataset.boardBand);
      }
    }
    return nothing;
  }

  /** Hit-test under the pointer. Pass `fromOverride` when resolving a drop
   * after the model has already cleared `dragFrom`, so the source card is
   * still skipped by the nearest-cell gutter fallback. */
  function cellAtPoint(
    clientX: number,
    clientY: number,
    fromOverride: Maybe<BoardDragSource>,
  ): Maybe<BoardHit> {
    const from = isJust(fromOverride) ? fromOverride : model.value.dragFrom;
    const arcMomentDrag =
      isJust(from) &&
      (from.value.kind === "arc-moment" || from.value.kind === "entity");
    const candidates = Array.from(
      document.querySelectorAll<HTMLElement>("[data-board-key]"),
    ).filter((el) => {
      if (
        arcMomentDrag === false &&
        el.closest(".zw-storyboard-filtered") !== null
      )
        return false;
      if (el.classList.contains("zw-storyboard-end-drop-idle")) return false;
      if (
        el.dataset.boardKind === "chapter-boundary" ||
        el.dataset.boardKey?.startsWith("act-end:") === true
      ) {
        return isJust(from) && from.value.kind === "chapter";
      }
      if (
        isNothing(from) ||
        (from.value.kind !== "chapter" &&
          from.value.kind !== "unplaced-chapter")
      )
        return true;
      if (el.closest(".zw-storyboard-timeline") === null) return true;
      return (
        el.dataset.boardKind === "chapter" ||
        el.dataset.boardKind === "chapter-boundary" ||
        el.dataset.boardKind === "act" ||
        el.dataset.boardKind === "end-of-manuscript"
      );
    });

    // Timeline scenes target boundaries. Include the source row so lifting
    // without moving cannot snap to its nearest sibling.
    if (
      isJust(from) &&
      (from.value.kind === "scene" || from.value.kind === "unplaced-scene")
    ) {
      const sceneCells = candidates.filter(
        (el) =>
          el.dataset.boardKind === "scene" &&
          el.closest(".zw-storyboard-timeline") !== null,
      );
      for (const el of sceneCells) {
        const rect = el.getBoundingClientRect();
        if (
          clientX < rect.left ||
          clientX > rect.right ||
          clientY < rect.top ||
          clientY > rect.bottom
        )
          continue;
        if (el.dataset.boardKey === from.value.key) return elementToHit(el);
        let target = el;
        if (clientY >= rect.top + rect.height / 2) {
          const next = toMaybe(
            candidates.find(
              (candidate) =>
                candidate.dataset.boardFolder === el.dataset.boardFolder &&
                (candidate.dataset.boardKind === "scene" ||
                  candidate.dataset.boardKind === "chapter-tail") &&
                candidate.getBoundingClientRect().top >= rect.bottom - 1,
            ),
          );
          if (isJust(next)) target = next.value;
        }
        const hit = elementToHit(target);
        return mapMaybe(hit, (value) => ({ ...value, insertion: true }));
      }
    }

    /** Cells that belong to the chapter (or act) being dragged. Hitting them
     * would no-op at commit (`from.folder === to.folder`) and, worse, the
     * nearest-cell gutter fallback prefers them over the end-of-manuscript
     * zone sitting just below a trailing act. */
    function isDragSourceCell(el: HTMLElement): boolean {
      if (isNothing(from)) return false;
      const source = from.value;
      if (isNothing(source.folder) || source.folder.value.length === 0) {
        return el.dataset.boardKey === source.key;
      }
      if (el.dataset.boardKey === source.key) return true;
      if (source.kind === "chapter" || source.kind === "act") {
        return (
          isJust(source.folder) &&
          el.dataset.boardFolder === source.folder.value
        );
      }
      return false;
    }

    for (const el of candidates) {
      if (isDragSourceCell(el)) continue;
      const rect = el.getBoundingClientRect();
      if (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      ) {
        return elementToHit(el);
      }
    }

    // Arc-moment drags only commit on strict hits. The nearest-cell gutter
    // fallback would otherwise snap a sidebar drop onto the board card
    // sitting just to the left of the panel.
    if (arcMomentDrag) return nothing;

    // No strict hit. Fall back to nearest cell, restricted to whichever
    // band (if any) the pointer is vertically inside. The end-of-manuscript
    // zone is exempt from the band filter: it lives outside every chapter
    // band on purpose.
    const band = bandFolderAtY(clientY);
    let best: Maybe<{ el: HTMLElement; distSq: number }> = nothing;
    for (const el of candidates) {
      if (isDragSourceCell(el)) continue;
      const isEndZone =
        el.dataset.boardKind === "end-of-manuscript" ||
        el.dataset.boardKey === END_OF_MANUSCRIPT_DROP_TARGET;
      if (
        isEndZone === false &&
        isJust(band) &&
        el.dataset.boardFolder !== band.value
      ) {
        continue;
      }
      const rect = el.getBoundingClientRect();
      const dx = Math.max(rect.left - clientX, 0, clientX - rect.right);
      const dy = Math.max(rect.top - clientY, 0, clientY - rect.bottom);
      const distSq = dx * dx + dy * dy;
      if (isNothing(best) || distSq < best.value.distSq) {
        best = just({ el, distSq });
      }
    }
    if (isNothing(best) || Math.sqrt(best.value.distSq) > NEAREST_CELL_MAX_PX) {
      return nothing;
    }
    const nearest = elementToHit(best.value.el);
    return mapMaybe(nearest, (hit) => ({
      ...hit,
      ...(best.value.el.closest(".zw-storyboard-timeline") !== null &&
      hit.kind === "scene"
        ? { insertion: true }
        : {}),
    }));
  }

  function elementToHit(el: HTMLElement): Maybe<BoardHit> {
    const keyMaybe = toMaybe(el.dataset.boardKey);
    if (isNothing(keyMaybe) || keyMaybe.value.length === 0) return nothing;
    const key = keyMaybe.value;
    const kind = el.dataset.boardKind;
    const emptyFields = {
      folder: nothing,
      actFolder: nothing,
      sceneFile: nothing,
      name: nothing,
      insertion: false,
    };
    if (kind === "arc-moment-tray") {
      return just({ key, kind: "arc-moment-tray", ...emptyFields });
    }
    if (kind === "unplaced-tray") {
      return just({ key, kind: "unplaced-tray", ...emptyFields });
    }
    if (kind === "unplaced-item") {
      return just({
        key,
        kind: "unplaced-item",
        ...emptyFields,
        name: toMaybe(el.dataset.boardName),
      });
    }
    if (kind === "end-of-manuscript" || key === END_OF_MANUSCRIPT_DROP_TARGET) {
      return just({
        key: END_OF_MANUSCRIPT_DROP_TARGET,
        kind: "end-of-manuscript",
        folder: just(END_OF_MANUSCRIPT_DROP_TARGET),
        actFolder: nothing,
        sceneFile: nothing,
        name: nothing,
        insertion: false,
      });
    }
    const folderMaybe = toMaybe(el.dataset.boardFolder);
    if (isNothing(folderMaybe) || folderMaybe.value.length === 0) {
      return nothing;
    }
    const actFolder = toMaybe(el.dataset.boardAct);
    if (kind === "chapter-boundary") {
      return just({
        key,
        kind: "chapter-boundary",
        folder: folderMaybe,
        actFolder: nothing,
        sceneFile: nothing,
        name: nothing,
        insertion: false,
      });
    }
    if (kind === "act") {
      return just({
        key,
        kind: "act",
        folder: folderMaybe,
        actFolder: nothing,
        sceneFile: nothing,
        name: nothing,
        insertion: false,
      });
    }
    if (kind === "scene") {
      return just({
        key,
        kind: "scene",
        folder: folderMaybe,
        actFolder,
        sceneFile: toMaybe(el.dataset.boardScene),
        name: nothing,
        insertion: false,
      });
    }
    if (kind === "chapter-tail") {
      return just({
        key,
        kind: "chapter-tail",
        folder: folderMaybe,
        actFolder,
        sceneFile: nothing,
        name: nothing,
        insertion: false,
      });
    }
    return just({
      key,
      kind: "chapter",
      folder: folderMaybe,
      actFolder,
      sceneFile: nothing,
      name: nothing,
      insertion: false,
    });
  }

  /** Direct DOM lookup by key. Used to resolve `pointercancel` (no final
   * coordinates) against whatever `dragOver` last pointed at, whether that's
   * a board cell or an Unplaced-panel element. */
  function hitFromKey(key: string): Maybe<BoardHit> {
    const el = document.querySelector<HTMLElement>(
      `[data-board-key="${CSS.escape(key)}"]`,
    );
    if (el === null) return nothing;
    return mapMaybe(elementToHit(el), (hit) => ({
      ...hit,
      ...(el.closest(".zw-storyboard-timeline") !== null && hit.kind === "scene"
        ? { insertion: true }
        : {}),
    }));
  }

  function arcScenePathForHit(hit: BoardHit): Maybe<string> {
    if (hit.kind === "chapter-boundary") return nothing;
    return scenePathForArcMomentDrop(
      {
        kind: hit.kind,
        folder: hit.folder,
        sceneFile: hit.sceneFile,
      },
      chapters.value,
    );
  }

  async function applyEntityFields(
    entryFile: string,
    nextFields: Parameters<typeof updateProjectDatabaseEntryFields>[2],
  ): Promise<void> {
    const rootPath = fromMaybe(session.rootPath);
    if (rootPath === null) return;
    await updateProjectDatabaseEntryFields(rootPath, entryFile, nextFields);
    await outlineEntities.refreshDatabase();
    if (
      isJust(session.activeDatabase) &&
      session.activeDatabase.value === entryFile
    ) {
      await activeEntityFields.loadFields();
    }
  }

  async function unlinkArcMoment(
    entryFile: string,
    momentId: string,
  ): Promise<void> {
    const entry = toMaybe(
      outlineEntities.databaseEntryFields.find(
        (item) => item.name === entryFile,
      ),
    );
    if (isNothing(entry)) return;
    await applyEntityFields(
      entryFile,
      unlinkArcMomentFromScene(entry.value.fields, momentId),
    );
  }

  async function linkArcMoment(
    entryFile: string,
    momentId: string,
    scenePath: string,
  ): Promise<void> {
    const entry = toMaybe(
      outlineEntities.databaseEntryFields.find(
        (item) => item.name === entryFile,
      ),
    );
    if (isNothing(entry)) return;
    await applyEntityFields(
      entryFile,
      linkArcMomentToScene(entry.value.fields, momentId, scenePath),
    );
  }

  async function setSceneLink(
    entryFile: string,
    scenePath: string,
    linked: boolean,
  ): Promise<void> {
    const entry = toMaybe(
      outlineEntities.databaseEntryFields.find(
        (item) => item.name === entryFile,
      ),
    );
    if (isNothing(entry)) return;
    await applyEntityFields(
      entryFile,
      setEntitySceneLink(entry.value.fields, scenePath, linked),
    );
  }

  async function runCmd(cmd: StoryboardDragCmd): Promise<void> {
    switch (cmd.kind) {
      case "addCandidateListeners":
        window.addEventListener("pointermove", onCandidatePointerMove, {
          passive: false,
        });
        window.addEventListener("pointerup", onCandidatePointerUp);
        window.addEventListener("pointercancel", onCandidatePointerCancel);
        return;
      case "removeCandidateListeners":
        window.removeEventListener("pointermove", onCandidatePointerMove);
        window.removeEventListener("pointerup", onCandidatePointerUp);
        window.removeEventListener("pointercancel", onCandidatePointerCancel);
        return;
      case "addDragListeners":
        window.addEventListener("pointermove", onPointerMove, {
          passive: false,
        });
        window.addEventListener("pointerup", onPointerUp);
        window.addEventListener("pointercancel", onPointerCancel);
        return;
      case "removeDragListeners":
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        window.removeEventListener("pointercancel", onPointerCancel);
        return;
      case "measureDragOver": {
        const hit = cellAtPoint(cmd.x, cmd.y, nothing);
        const arcScenePath = isJust(hit)
          ? arcScenePathForHit(hit.value)
          : nothing;
        await send({ type: "DragOverMeasured", hit, arcScenePath });
        return;
      }
      case "resolveDrop": {
        const x = mapMaybe(cmd.point, (p) => p.x);
        const y = mapMaybe(cmd.point, (p) => p.y);
        const hit =
          isJust(x) && isJust(y)
            ? cellAtPoint(x.value, y.value, just(cmd.from))
            : isJust(cmd.overKey)
              ? hitFromKey(cmd.overKey.value)
              : nothing;
        if (isNothing(hit)) return;
        await send({
          type: "DropResolved",
          from: cmd.from,
          hit: hit.value,
          arcScenePath: arcScenePathForHit(hit.value),
          chapters: chapters.value,
          manuscript: session.manuscript,
        });
        return;
      }
      case "moveChapterToSlot":
        void workspace.onMoveChapterToSlot(cmd.chapterFolder, {
          actFolder: cmd.actFolder,
          beforeFolder: cmd.beforeFolder,
        });
        return;
      case "moveAct":
        void workspace.onMoveAct(cmd.actFolder, cmd.before);
        return;
      case "reorderScenes":
        void workspace.onReorderProjectScenes(cmd.from, cmd.to);
        return;
      case "parkScene":
        void workspace.onParkScene(cmd.folder, cmd.sceneFile);
        return;
      case "parkChapter":
        void workspace.onParkChapter(cmd.folder);
        return;
      case "unparkScene": {
        await workspace.onUnparkSceneIntoChapter(
          cmd.parkedName,
          cmd.toFolder,
          nothing,
        );
        if (isJust(cmd.reorderBefore)) {
          await workspace.onReorderProjectScenes(
            { folder: cmd.toFolder, sceneFile: cmd.parkedName },
            { folder: cmd.toFolder, sceneFile: cmd.reorderBefore },
          );
        }
        return;
      }
      case "unparkChapter":
        await workspace.onUnparkChapter(cmd.parkedName, cmd.atIndex);
        return;
      case "unlinkArcMoment":
        await unlinkArcMoment(cmd.entryFile, cmd.momentId);
        return;
      case "linkEntity": {
        await setSceneLink(cmd.entryFile, cmd.scenePath, true);
        return;
      }
      case "linkArcMoment":
        await linkArcMoment(cmd.entryFile, cmd.momentId, cmd.scenePath);
        return;
      default:
        assertNever(cmd);
    }
  }

  function onPointerMove(event: PointerEvent): void {
    if (isNothing(model.value.dragFrom)) return;
    event.preventDefault();
    void send({
      type: "DragPointerMoved",
      x: event.clientX,
      y: event.clientY,
    });
  }

  function onPointerUp(event: PointerEvent): void {
    void send({
      type: "DragPointerEnded",
      point: just({ x: event.clientX, y: event.clientY }),
    });
  }

  function onPointerCancel(): void {
    void send({ type: "DragPointerEnded", point: nothing });
  }

  function onCandidatePointerMove(event: PointerEvent): void {
    if (candidatePromotes(model.value, event.clientX, event.clientY)) {
      event.preventDefault();
    }
    void send({
      type: "CandidatePointerMoved",
      x: event.clientX,
      y: event.clientY,
    });
  }

  function onCandidatePointerUp(): void {
    void send({ type: "CandidatePointerEnded" });
  }

  function onCandidatePointerCancel(): void {
    void send({ type: "CandidatePointerEnded" });
  }

  /** Same interactive-control opt-out as `useOutlineDrag.ts`. Kept as a
   * defensive backstop even though every caller today only wires this to a
   * dedicated grip element that's never itself inside an editable field. */
  function onCardPointerDown(info: BoardDragSource, event: PointerEvent): void {
    const target = event.target as HTMLElement;
    void send({
      type: "CardPointerDown",
      info,
      button: event.button,
      x: event.clientX,
      y: event.clientY,
      targetIsEditable:
        target.closest("input, textarea, [contenteditable]") !== null,
    });
  }

  function consumePointerGestureWasDrag(): boolean {
    const wasDrag = model.value.gestureWasDrag;
    void send({ type: "GestureConsumed" });
    return wasDrag;
  }

  function cleanup(): void {
    void send({ type: "Cleanup" });
  }

  return {
    unlinkArcMoment,
    linkArcMoment,
    setSceneLink,
    dragFrom,
    dragOver,
    dragKind,
    onCardPointerDown,
    consumePointerGestureWasDrag,
    cleanup,
  };
});

if (typeof import.meta.hot !== "undefined") {
  import.meta.hot.accept(
    acceptHMRUpdate(useStoryboardDragStore, import.meta.hot),
  );
}
